import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';

const MapSidebar = ({ language, onFarmSelect, selectedFarm, onFilterChange }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [elevationRange, setElevationRange] = useState({ min: '', max: '' });
  const [certificationFilter, setCertificationFilter] = useState([]);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const farms = [
  {
    id: 1,
    name: { en: "Yirgacheffe Highland Farm", zh: "耶加雪菲高地农场" },
    country: { en: "Ethiopia", zh: "埃塞俄比亚" },
    supplier: { en: "Addis Coffee Exports", zh: "亚的斯咖啡出口" },
    altitude: 2100,
    image: "https://images.unsplash.com/photo-1722962883757-4ca3c1c9df56",
    imageAlt: "Aerial view of lush green Ethiopian coffee plantation on mountainous terrain with workers harvesting red coffee cherries under bright sunlight",
    area: "45 hectares",
    certifications: ["Organic", "Fair Trade"],
    associatedBeans: 3,
    coordinates: [38.2, 6.2]
  },
  {
    id: 2,
    name: { en: "Sidamo Valley Estate", zh: "西达摩谷庄园" },
    country: { en: "Ethiopia", zh: "埃塞俄比亚" },
    supplier: { en: "Addis Coffee Exports", zh: "亚的斯咖啡出口" },
    altitude: 1950,
    image: "https://images.unsplash.com/photo-1706041093100-6eb95b26478f",
    imageAlt: "Expansive Ethiopian coffee farm with rows of coffee plants stretching across rolling hills under clear blue sky with traditional farming structures visible",
    area: "62 hectares",
    certifications: ["Rainforest Alliance"],
    associatedBeans: 2,
    coordinates: [38.5, 6.5]
  },
  {
    id: 3,
    name: { en: "Mount Elgon Coffee Farm", zh: "埃尔贡山咖啡农场" },
    country: { en: "Uganda", zh: "乌干达" },
    supplier: { en: "Uganda Premium Beans", zh: "乌干达优质咖啡豆" },
    altitude: 1800,
    image: "https://images.unsplash.com/photo-1715138958579-4d4ea6eeafbb",
    imageAlt: "Ugandan coffee plantation on volcanic slopes with dense coffee bushes bearing ripe red cherries and farmers working among the plants",
    area: "38 hectares",
    certifications: ["Organic", "UTZ"],
    associatedBeans: 2,
    coordinates: [34.5, 1.2]
  },
  {
    id: 4,
    name: { en: "Rwenzori Mountain Farm", zh: "鲁文佐里山农场" },
    country: { en: "Uganda", zh: "乌干达" },
    supplier: { en: "Uganda Premium Beans", zh: "乌干达优质咖啡豆" },
    altitude: 1650,
    image: "https://images.unsplash.com/photo-1720800670279-2d0c566fc5e8",
    imageAlt: "Mountain coffee farm in Uganda with terraced coffee plantations cascading down hillsides surrounded by tropical vegetation and morning mist",
    area: "52 hectares",
    certifications: ["Fair Trade"],
    associatedBeans: 1,
    coordinates: [30.0, 0.5]
  },
  {
    id: 5,
    name: { en: "Nyeri Highlands Estate", zh: "涅里高地庄园" },
    country: { en: "Kenya", zh: "肯尼亚" },
    supplier: { en: "Kenya Coffee Traders", zh: "肯尼亚咖啡贸易商" },
    altitude: 1750,
    image: "https://images.unsplash.com/photo-1722962884239-ae1583767578",
    imageAlt: "Kenyan coffee estate with mature coffee trees laden with bright red cherries against backdrop of Mount Kenya peaks in distance",
    area: "41 hectares",
    certifications: ["Organic", "Rainforest Alliance"],
    associatedBeans: 2,
    coordinates: [37.0, -0.4]
  },
  {
    id: 6,
    name: { en: "Kiambu Coffee Cooperative", zh: "基安布咖啡合作社" },
    country: { en: "Kenya", zh: "肯尼亚" },
    supplier: { en: "Kenya Coffee Traders", zh: "肯尼亚咖啡贸易商" },
    altitude: 1600,
    image: "https://images.unsplash.com/photo-1629008642899-178df6fc5f2f",
    imageAlt: "Cooperative coffee farm in Kenya showing organized rows of coffee plants with farmers in traditional clothing harvesting ripe coffee cherries",
    area: "28 hectares",
    certifications: ["Fair Trade", "UTZ"],
    associatedBeans: 1,
    coordinates: [36.8, -1.2]
  },
  {
    id: 7,
    name: { en: "Plateau State Farm", zh: "高原州农场" },
    country: { en: "Nigeria", zh: "尼日利亚" },
    supplier: { en: "Nigerian Coffee Collective", zh: "尼日利亚咖啡集团" },
    altitude: 1400,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d05c2578-1764533603929.png",
    imageAlt: "Nigerian coffee plantation on plateau with coffee bushes growing under shade trees and local farmers tending to the crops in afternoon light",
    area: "35 hectares",
    certifications: ["Organic"],
    associatedBeans: 1,
    coordinates: [9.0, 9.5]
  },
  {
    id: 8,
    name: { en: "Mambilla Plateau Estate", zh: "曼比拉高原庄园" },
    country: { en: "Nigeria", zh: "尼日利亚" },
    supplier: { en: "Nigerian Coffee Collective", zh: "尼日利亚咖啡集团" },
    altitude: 1550,
    image: "https://images.unsplash.com/photo-1650156207068-0e1317662449",
    imageAlt: "High altitude Nigerian coffee estate with coffee plants growing on terraced slopes surrounded by indigenous trees and natural water sources",
    area: "47 hectares",
    certifications: ["Rainforest Alliance"],
    associatedBeans: 2,
    coordinates: [11.0, 7.0]
  }];


  const countryOptions = [
  { value: 'ethiopia', label: language === 'en' ? 'Ethiopia' : '埃塞俄比亚' },
  { value: 'uganda', label: language === 'en' ? 'Uganda' : '乌干达' },
  { value: 'kenya', label: language === 'en' ? 'Kenya' : '肯尼亚' },
  { value: 'nigeria', label: language === 'en' ? 'Nigeria' : '尼日利亚' }];


  const certificationOptions = [
  { value: 'organic', label: language === 'en' ? 'Organic' : '有机认证' },
  { value: 'fairtrade', label: language === 'en' ? 'Fair Trade' : '公平贸易' },
  { value: 'rainforest', label: language === 'en' ? 'Rainforest Alliance' : '雨林联盟' },
  { value: 'utz', label: 'UTZ' }];


  const filteredFarms = farms?.filter((farm) => {
    const matchesSearch = farm?.name?.[language]?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    farm?.supplier?.[language]?.toLowerCase()?.includes(searchQuery?.toLowerCase());

    const matchesCountry = selectedCountries?.length === 0 ||
    selectedCountries?.includes(farm?.country?.en?.toLowerCase());

    const matchesElevation = (!elevationRange?.min || farm?.altitude >= parseInt(elevationRange?.min)) && (
    !elevationRange?.max || farm?.altitude <= parseInt(elevationRange?.max));

    const matchesCertification = certificationFilter?.length === 0 ||
    certificationFilter?.some((cert) =>
    farm?.certifications?.some((farmCert) =>
    farmCert?.toLowerCase()?.includes(cert?.toLowerCase())
    )
    );

    return matchesSearch && matchesCountry && matchesElevation && matchesCertification;
  });

  useEffect(() => {
    onFilterChange({
      countries: selectedCountries,
      elevation: elevationRange,
      certifications: certificationFilter
    });
  }, [selectedCountries, elevationRange, certificationFilter]);

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedCountries([]);
    setElevationRange({ min: '', max: '' });
    setCertificationFilter([]);
  };

  const sidebarContent =
  <div className="h-full flex flex-col bg-card">
      <div className="p-4 border-b border-border">
        <h2 className="text-lg font-heading font-semibold text-foreground mb-4">
          {language === 'en' ? 'Farm Locations' : '农场位置'}
        </h2>
        
        <Input
        type="search"
        placeholder={language === 'en' ? 'Search farms or suppliers...' : '搜索农场或供应商...'}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e?.target?.value)}
        className="mb-4" />


        <div className="space-y-4">
          <Select
          label={language === 'en' ? 'Filter by Country' : '按国家筛选'}
          options={countryOptions}
          value={selectedCountries}
          onChange={setSelectedCountries}
          multiple
          searchable
          clearable
          placeholder={language === 'en' ? 'Select countries' : '选择国家'} />


          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {language === 'en' ? 'Elevation Range (meters)' : '海拔范围（米）'}
            </label>
            <div className="grid grid-cols-2 gap-2">
              <Input
              type="number"
              placeholder={language === 'en' ? 'Min' : '最小'}
              value={elevationRange?.min}
              onChange={(e) => setElevationRange((prev) => ({ ...prev, min: e?.target?.value }))} />

              <Input
              type="number"
              placeholder={language === 'en' ? 'Max' : '最大'}
              value={elevationRange?.max}
              onChange={(e) => setElevationRange((prev) => ({ ...prev, max: e?.target?.value }))} />

            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {language === 'en' ? 'Certifications' : '认证'}
            </label>
            <div className="space-y-2">
              {certificationOptions?.map((cert) =>
            <Checkbox
              key={cert?.value}
              label={cert?.label}
              checked={certificationFilter?.includes(cert?.value)}
              onChange={(e) => {
                if (e?.target?.checked) {
                  setCertificationFilter([...certificationFilter, cert?.value]);
                } else {
                  setCertificationFilter(certificationFilter?.filter((c) => c !== cert?.value));
                }
              }} />

            )}
            </div>
          </div>

          <Button
          variant="outline"
          fullWidth
          iconName="X"
          iconPosition="left"
          onClick={handleClearFilters}>

            {language === 'en' ? 'Clear Filters' : '清除筛选'}
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        <div className="text-sm text-muted-foreground mb-3">
          {language === 'en' ?
        `${filteredFarms?.length} farms found` :
        `找到 ${filteredFarms?.length} 个农场`}
        </div>
        
        <div className="space-y-3">
          {filteredFarms?.map((farm) =>
        <div
          key={farm?.id}
          onClick={() => onFarmSelect(farm)}
          className={`p-3 rounded-lg border cursor-pointer transition-smooth ${
          selectedFarm?.id === farm?.id ?
          'border-primary bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-muted'}`
          }>

              <div className="flex gap-3">
                <div className="w-20 h-20 rounded-md overflow-hidden flex-shrink-0">
                  <Image
                src={farm?.image}
                alt={farm?.imageAlt}
                className="w-full h-full object-cover" />

                </div>
                
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-foreground text-sm mb-1 truncate">
                    {farm?.name?.[language]}
                  </h3>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
                    <Icon name="MapPin" size={12} />
                    <span className="truncate">{farm?.country?.[language]}</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
                    <Icon name="Mountain" size={12} />
                    <span>{farm?.altitude}m</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Icon name="Package" size={12} />
                    <span>
                      {farm?.associatedBeans} {language === 'en' ? 'beans' : '种咖啡豆'}
                    </span>
                  </div>
                </div>
              </div>
              
              {farm?.certifications?.length > 0 &&
          <div className="flex flex-wrap gap-1 mt-2">
                  {farm?.certifications?.map((cert, idx) =>
            <span
              key={idx}
              className="px-2 py-0.5 bg-success/10 text-success text-xs rounded-full">

                      {cert}
                    </span>
            )}
                </div>
          }
            </div>
        )}
        </div>
      </div>
    </div>;


  return (
    <>
      <Button
        variant="default"
        className="lg:hidden fixed bottom-4 right-4 z-[60] shadow-modal"
        iconName="List"
        onClick={() => setIsMobileOpen(true)}>

        {language === 'en' ? 'Farm List' : '农场列表'}
      </Button>

      <div className="hidden lg:block w-80 h-full border-r border-border overflow-hidden">
        {sidebarContent}
      </div>

      {isMobileOpen &&
      <div className="lg:hidden fixed inset-0 z-[70]">
          <div
          className="absolute inset-0 bg-background/80 backdrop-blur-sm"
          onClick={() => setIsMobileOpen(false)} />

          <div className="absolute bottom-0 left-0 right-0 bg-card rounded-t-2xl shadow-modal max-h-[80vh] overflow-hidden animate-slide-up">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h2 className="text-lg font-heading font-semibold text-foreground">
                {language === 'en' ? 'Farm Locations' : '农场位置'}
              </h2>
              <button
              onClick={() => setIsMobileOpen(false)}
              className="p-2 hover:bg-muted rounded-md transition-smooth">

                <Icon name="X" size={20} />
              </button>
            </div>
            <div className="overflow-y-auto" style={{ maxHeight: 'calc(80vh - 60px)' }}>
              {sidebarContent}
            </div>
          </div>
        </div>
      }
    </>);

};

export default MapSidebar;